import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from 'react-redux';
import { notification, Button, Space } from "antd";
import { FormOutlined, BookOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery,
         invalidateRoutersLookup } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function RouterManufactureList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const dispatch = useDispatch();
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();

  // ===== 3. DERIVADOS =====
  const selectedUuid = data.selected?.uuid || "";

  const modelFilter = useMemo(
    () => models
      .filter(model => model.device_type === 'router')
      .map(model => ({ text: model.name, value: model.uuid })),
    [models]
  );

  const versionFilter = useMemo(
    () => versions
      .filter(version => version.device_type === 'router')
      .map(version => ({ text: version.name, value: version.uuid })),
    [versions]
  );

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("routers/manufacture/Fabricado");

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);

  const enrichedData = useMemo(() => {
    return data.raw.map((element) => ({
      ...element,
      dateFormated: formatDate(element.date) || '-',
    }));
  }, [data.raw]);

  // ===== 6. HANDLERS =====
  const deleteRouter = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del(`routers/${selectedUuid}`);
      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
      } else {
        notification["success"]({ message: lang.handle('Router borrado correctamente') });
        dispatch(invalidateRoutersLookup());
      }
      await Pfetch();
      updateData('selected', null);
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  };

  const updateRouter = () => navigate(`/boards/manufacture/edit/3/${selectedUuid}`);
  const assignRouter = () => navigate(`/boards/manufacture/assign/3/${selectedUuid}`);

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => updateUI('loading', false));
  }, [Pfetch]);

  // ===== 8. CONFIG =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    {
      title: "IMEI",
      dataIndex: "modem_imei",
      ...GetColumnLiteralSearchProps("modem_imei", lang),
    },
    {
      title: lang.handle('Tipo Teltonika'),
      dataIndex: "modem_type_code",
      ...GetColumnLiteralSearchProps("modem_type_code", lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: "modem_serial_number",
      ...GetColumnLiteralSearchProps("modem_serial_number", lang),
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: "model_name",
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
    },
        {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "dateFormated",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
    },
  ];

  let selection = null;
    if (hasPrivilege('manufacture_delete') || hasPrivilege('manufacture_edit') || hasPrivilege('assign')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selected?.uuid ? [data.selected.uuid] : [],
      onChange: onSelectionChange,
    };
  }

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={selection}
        columns={columns}
        rowKey="uuid"
        dataSource={enrichedData}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('manufacture_delete') && (
            <DeleteButton onEvent={deleteRouter} buttonDisabled={!data.selected} />
          )}

          {hasPrivilege('manufacture_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={updateRouter}
            >
              {lang.handle('Editar')}
            </Button>
          )}
          {hasPrivilege('assign') && (
            <Button
              type="primary"
              icon={<BookOutlined />}
              disabled={!data.selected}
              onClick={assignRouter}
            >
              {lang.handle('Asignar')}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(RouterManufactureList));
